// Pure draft reducer + validation for the CUSTOM alarm builder (custom +
// conditional alarms, phase 5). Mirrors `alarmDefsForm.ts` — the recipe form's
// reducer — so both authoring surfaces read the same way: a plain draft object,
// pure field actions that return a new draft, a `validate` that returns field
// errors, and a body builder for the POST.
//
// Two things make the custom draft different from the recipe draft:
//
//   1. There is no recipe, so there is no param metadata over the wire. A custom
//      alarm pins ONE catalog path to ONE trigger class, and its numeric params
//      ride `thresholdsByMode` under the reserved keys the compiler reads
//      (`setpoint` / `clearpoint` / `setDelayMs` / `clearDelayMs` / `staleMs` —
//      ADR-097). The draft holds SECONDS because that is what an operator types;
//      {@link toCreateBody} converts to the milliseconds the wire uses.
//
//   1b. Phase 6 adds the "Only when…" rows: 0..4 condition sub-drafts plus an
//      all/any combinator, mapped on submit to the wire's
//      `conditions {logic, clauses}` block (above→High, below→Low, on→Active,
//      off→Inactive). A custom alarm carries EXPLICIT path clauses only — the
//      `engine_running` preset is a recipe affordance the API rejects here.
//
//   1c. Phase 7 reads a STORED definition back into a draft
//      ({@link initFromCustomDef}) so a custom alarm can be edited in place, and
//      diffs the edited draft against it ({@link toPatchBody}). Same reducer,
//      same validation, same error mapping as the create path — an editor that
//      re-implemented any of those would drift from the builder.
//
//   2. v1 compiles ONE threshold block per custom alarm. The API rejects
//      divergent per-mode blocks outright, so {@link toCreateBody} fans a single
//      authored block out across every enabled mode — identical copies, which
//      the API accepts and the compiler reads once.
//
// STORAGE AND THE WIRE ARE VERBATIM STORE UNITS (plan D6): whatever the boat
// declares for the picked signal. Every number this module hands to the API —
// {@link toCreateBody}, {@link toPatchBody}, {@link draftThresholds} — is in
// that unit, and every comparison it makes against a live reading is too. That
// is not negotiable: it is what reintroduced the Kelvin-class bug last time.
//
// PRESENTATION IS THE ONE EXCEPTION (ADR 0189 §4). An account that reads depth
// in fathoms types its set point in fathoms, because a box labelled `ftm` beside
// a live value printed in `ftm` cannot honestly hold metres. So the two value
// boxes — set point and clear point — hold the ACCOUNT's unit whenever the
// pinned signal has a category ({@link categoryFor}, path-based here: a custom
// alarm has no recipe param key to look up) and the account's unit for that
// category differs from the boat's. Conversion happens at exactly two edges:
// {@link initFromCustomDef} reads stored→box, and {@link thresholdBlock} writes
// box→stored. Everything between them moves strings around.
//
// THE BASELINE GUARD is what makes that safe. A converted rendering does not
// round-trip — a stored 20 m reads "66 ft", and 66 ft is 20.1168 m — so
// {@link initFromCustomDef} records, per box, the exact TEXT it put there beside
// the stored NUMBER that text came from. {@link draftSetpointStored} re-emits
// that original number verbatim for any box still holding its baseline text (or
// a numerically equal retype), with no arithmetic at all.
//
// This is load-bearing, not a nicety. `thresholdsByMode` is ALL-OR-NOTHING: the
// API replaces the whole map, so editing a delay re-sends the set point beside
// it. Without the guard that re-send would be a rounding artefact — a threshold
// nobody touched, changed by 0.1168 m, moving the boat's `defsDigest` and
// resyncing the entire ruleset. Every path that turns a box into a number goes
// through the one resolver for that reason.
//
// A CREATE needs no guard: nothing is stored yet, so the typed value simply
// converts once on the way out.

import type { UnitSettings } from '@neomaris/protocol';
import { parseUnitSettings } from '@neomaris/protocol';
import { CANONICAL_MODES, type NotificationUrgency } from '@neomaris/rules';

import { ALARM_SEVERITIES, readCustomSpec, type AlarmSeverity } from './alarmDisplay';
import { convertedQuantity, readConditions, type ConditionClauseSpec } from './alarmSentence';
import { categoryFor, convertQuantity, type UnitCategory } from './displayUnits';
import {
  freshness,
  suggestedTriggerClasses,
  type Signal,
  type TriggerClass,
} from './signalCatalog';
import { toggleWatchGridMode } from './watchSchedule';

/**
 * The modes a custom alarm can watch. A custom alarm has no recipe defaults, so
 * the universe is the shared canonical vocabulary and a fresh draft starts with
 * all of them enabled ("watch always") — the operator narrows it.
 */
export const CUSTOM_MODE_UNIVERSE: readonly string[] = CANONICAL_MODES;

/** Longest delay/staleness window the builder accepts, in seconds (24 h). */
export const MAX_DELAY_SECONDS = 86_400;

/**
 * What the three timing fields are CALLED. One object, because both custom
 * editors (`AlarmNewCustom`, `CustomAlarmEditForm`) render the same three
 * fields and a rename that reached one and not the other would have the same
 * alarm described two ways depending on where you opened it.
 *
 * These are also the wording the shared registry's custom-trigger metadata now
 * mirrors (`CUSTOM_SET_DELAY_PARAM` and friends in `@neomaris/rules`), so a
 * delay authored from a recipe and one authored by hand read alike. That mirror
 * crosses a package boundary and cannot be a shared constant; changing a string
 * here means changing it there too.
 */
export const CUSTOM_TIMING_LABELS = {
  setDelay: 'Wait before setting off',
  clearDelay: 'Wait before clearing',
  stale: 'Stops reporting for',
} as const;

/** The API's `title` bound (`z.string().min(1).max(200)`). */
export const MAX_TITLE_LENGTH = 200;

/**
 * How many condition rows one alarm can carry. The shared schema
 * (`alertConditionsSchema`) caps the clause list at 4 in v1, so the builder
 * stops offering "Add another condition" there rather than letting the API say
 * no after the fact.
 */
export const MAX_CONDITIONS = 4;

/**
 * The staleness window a `DataUnavailable` custom alarm gets when none is
 * authored. Mirrors the compiler's `DEFAULT_CUSTOM_STALE_MS` (5 min) — kept in
 * sync by the sentence tests, since the value is module-private there.
 */
export const DEFAULT_STALE_SECONDS = 300;

/**
 * The notification urgencies the builder offers, in plain-English order.
 *
 * `silent` and `escalating` are in the shared vocabulary but deliberately NOT
 * offered: `silent` does not mute the boat's buzzer (urgency only routes the
 * cloud email), and `escalating` chains are unwired today, so both would be
 * labels that lie. A stored definition carrying either still round-trips.
 */
export const NOTIFY_CHOICES: readonly NotificationUrgency[] = ['local', 'remote'];

/** The trigger-card headline for each class — plain English, no wire words. */
export function triggerLabel(triggerClass: TriggerClass): string {
  switch (triggerClass) {
    case 'High':
      return 'Goes above';
    case 'Low':
      return 'Drops below';
    case 'On':
      return 'Turns on';
    case 'Off':
      return 'Turns off';
    case 'DataUnavailable':
      return 'Stops reporting';
  }
}

/** One line under the headline, saying what the boat will actually watch for. */
export function triggerHelp(triggerClass: TriggerClass): string {
  switch (triggerClass) {
    case 'High':
      return 'Sets off when the reading rises past a number you choose.';
    case 'Low':
      return 'Sets off when the reading falls past a number you choose.';
    case 'On':
      return 'Sets off when this turns on.';
    case 'Off':
      return 'Sets off when this turns off.';
    case 'DataUnavailable':
      return 'Sets off when nothing is reported for as long as you choose.';
  }
}

/** Plain-English label for a notification urgency the builder offers. */
export function notifyLabel(urgency: NotificationUrgency): string {
  switch (urgency) {
    case 'local':
      return 'Sound the alarm on the boat';
    case 'remote':
      return 'Sound the alarm on the boat and email me';
    case 'escalating':
      return 'Sound the alarm on the boat and keep emailing me';
    case 'silent':
      return 'No email';
  }
}

// --- conditions ("Only when…") ---------------------------------------------

/**
 * What one condition row checks, in the operator's words. Each maps to exactly
 * one wire clause type: `above`→`High`, `below`→`Low`, `on`→`Active`,
 * `off`→`Inactive` (see {@link draftConditions}).
 */
export type ConditionOperator = 'above' | 'below' | 'on' | 'off';

/** How the condition rows fold together — "all must be true" / "any can be". */
export type ConditionLogic = 'all' | 'any';

/**
 * One editable condition row. Every field is optional because a row exists from
 * the moment the operator clicks "Add another condition" — an empty row is a
 * draft state the form shows errors for, not an illegal object.
 */
export interface ConditionDraft {
  /** The catalog path this condition watches. */
  readonly signalPath?: string;
  readonly operator?: ConditionOperator;
  /** The comparison value for `above`/`below`, in store units. Raw string. */
  readonly value?: string;
}

/**
 * The operators a signal kind can carry, mirroring
 * {@link suggestedTriggerClasses}' kind rules: numbers compare against a value,
 * booleans are on or off.
 *
 * Everything else — text, structured (`object`), an accepted path with no sample
 * yet (`unknown`), or a kind from a newer build — returns EMPTY: there is no
 * honest clause to make from it. `DataUnavailable` has no clause type at all
 * (the wire clause set is High/Low/Active/Inactive), so "stops reporting" cannot
 * be a condition in v1 even on a numeric signal.
 */
export function conditionOperatorsFor(kind: string): ConditionOperator[] {
  if (kind === 'number') return ['above', 'below'];
  if (kind === 'boolean') return ['on', 'off'];
  return [];
}

/** Can this signal kind be a condition at all? */
export function canConditionOn(kind: string): boolean {
  return conditionOperatorsFor(kind).length > 0;
}

/**
 * The catalog subset a condition picker may offer. Filtering at the PICKER
 * rather than only at validation matters: offering a text signal and then
 * refusing every operator for it reads as a broken form.
 */
export function conditionSignals(catalog: readonly Signal[]): Signal[] {
  return catalog.filter((s) => canConditionOn(s.kind));
}

/**
 * The editable draft for one custom alarm. Numeric fields are RAW STRINGS (what
 * the input holds) and are parsed on validate/submit, exactly like the recipe
 * draft's `values` — so a half-typed "-" or "1." is a draft state, not a crash.
 */
export interface CustomAlarmDraft {
  /** The pinned catalog path. Absent ⇒ no signal picked yet. */
  readonly signalPath?: string;
  /** Absent ⇒ no trigger chosen yet. */
  readonly triggerClass?: TriggerClass;
  /**
   * Set point, IN THE UNIT THE BOX SHOWS (High/Low only) — the account's when
   * one applies, the signal's store unit otherwise. See the units note at the
   * top of this file; resolve it with {@link draftSetpointStored}, never with
   * `Number(draft.setpoint)`.
   */
  readonly setpoint?: string;
  /**
   * Optional clear point, same unit as {@link setpoint}. Absent ⇒ no hysteresis.
   */
  readonly clearpoint?: string;
  /** Seconds the trigger must hold before the alarm fires. */
  readonly setDelaySeconds: string;
  /** Seconds the clear condition must hold before the alarm clears. */
  readonly clearDelaySeconds: string;
  /** Silence window before a `DataUnavailable` alarm fires, in seconds. */
  readonly staleSeconds?: string;
  /** Required by the API — prefilled from the sentence until the operator edits it. */
  readonly title: string;
  /** True once the operator typed a name; stops the sentence prefill overwriting it. */
  readonly titleTouched: boolean;
  readonly severity: AlarmSeverity;
  /** The checked subset of {@link CUSTOM_MODE_UNIVERSE}, in universe order. */
  readonly enabledModes: string[];
  readonly notify: NotificationUrgency;
  /**
   * The "Only when…" rows, 0..{@link MAX_CONDITIONS}. Empty ⇒ the alarm is
   * unconditional and no `conditions` block is sent at all.
   */
  readonly conditions: ConditionDraft[];
  /** How the rows fold. `all` (AND) is the default; the UI offers the toggle at ≥2 rows. */
  readonly conditionLogic: ConditionLogic;
  /**
   * The account's display units (`useUnits()`), carried on the draft like the
   * recipe form's so this pure reducer can convert without a React context.
   *
   * Absent ⇒ every box shows the stored number, which is what a metric account
   * sees anyway and what every caller that predates ADR 0189 gets.
   */
  readonly units?: UnitSettings;
  /**
   * The pinned signal's DECLARED store unit, from the catalog row. Needed
   * beside {@link units} because the conversion only runs when the boat's own
   * declaration matches the category's store unit — the gate that keeps a depth
   * channel reporting volts in volts.
   */
  readonly storeUnit?: string | null;
  /**
   * What the value boxes opened holding — see the baseline note at the top of
   * this file. Only recorded for a box a stored number actually filled AND only
   * when a conversion applies: an unconverted box round-trips its own text
   * exactly, so there is nothing for a guard to protect.
   */
  readonly baseline?: CustomValueBaselines;
}

/**
 * One value box's original state: the TEXT the draft opened with, and the
 * stored NUMBER that text was rendered from. Always a pair, because
 * {@link draftSetpointStored} answers with the number whenever it still sees
 * the text.
 */
export interface CustomValueBaseline {
  readonly text: string;
  readonly stored: number;
}

/** The two boxes an account conversion can reach. Delays are never converted. */
export interface CustomValueBaselines {
  readonly setpoint?: CustomValueBaseline;
  readonly clearpoint?: CustomValueBaseline;
}

/**
 * A fresh, empty CREATE draft.
 *
 * `units` is the account's settings, carried so the value box the operator
 * eventually fills is read in their own unit and converted once on the POST.
 * Omit it and every box is the stored number, exactly as before ADR 0189.
 */
export function initCustomDraft(units?: UnitSettings): CustomAlarmDraft {
  return {
    setDelaySeconds: '0',
    clearDelaySeconds: '0',
    staleSeconds: String(DEFAULT_STALE_SECONDS),
    title: '',
    titleTouched: false,
    severity: 'warning',
    enabledModes: [...CUSTOM_MODE_UNIVERSE],
    notify: 'remote',
    conditions: [],
    conditionLogic: 'all',
    ...(units === undefined ? {} : { units }),
  };
}

// --- the account's unit for the value boxes (ADR 0189 §4) -------------------

/** Store ⇄ account-unit arithmetic for one category and one chosen unit. */
interface ValueConversion {
  readonly toEdited: (stored: number) => number;
  readonly toStored: (edited: number) => number;
}

/**
 * The inverse of `displayUnits`' conversion table, for every account unit a
 * custom alarm's value box can be read in. Entries exist only where the account
 * unit DIFFERS from the store unit; anything not listed takes the identity path.
 *
 * Wider than the recipe form's table (`alertTemplates`) on purpose. A recipe
 * param converts by KEY, and only four keys have a category; a custom alarm
 * converts by PATH, and `categoryFor` reaches speed (SOG, STW, wind), pressure
 * (`environment.*.pressure` — never engine oil pressure, which the path rules
 * exclude), volume and short distance as well. A category the gate fires for
 * with no entry here would show a store-unit box beside an account-unit reading,
 * which is the mixed-unit failure this whole change exists to prevent — so the
 * tests pin that every firing pair has one.
 *
 * Each `toStored` is the exact algebraic inverse of the matching `toEdited`,
 * written out rather than derived from probes of the forward function: the
 * affine slope for °C→°F recovers as 1.7999999999999972, which stores 212 °F as
 * 100.00000000000016 °C. No rounding either — the boat declares no grain for a
 * depth or a temperature, and inventing one here is not this module's business.
 */
const CATEGORY_CONVERSIONS: {
  readonly [K in UnitCategory]: Partial<Record<UnitSettings[K], ValueConversion>>;
} = {
  distance: {
    nm: { toEdited: (v) => v / 1852, toStored: (v) => v * 1852 },
    mi: { toEdited: (v) => v / 1609.344, toStored: (v) => v * 1609.344 },
    km: { toEdited: (v) => v / 1000, toStored: (v) => v * 1000 },
  },
  shortDistance: {
    ft: { toEdited: (v) => v / 0.3048, toStored: (v) => v * 0.3048 },
  },
  speed: {
    mph: { toEdited: (v) => v * 1.15078, toStored: (v) => v / 1.15078 },
    kmh: { toEdited: (v) => v * 1.852, toStored: (v) => v / 1.852 },
  },
  depth: {
    ft: { toEdited: (v) => v / 0.3048, toStored: (v) => v * 0.3048 },
    ftm: { toEdited: (v) => v / 1.8288, toStored: (v) => v * 1.8288 },
  },
  temperature: {
    F: { toEdited: (v) => v * 1.8 + 32, toStored: (v) => (v - 32) / 1.8 },
  },
  pressure: {
    inHg: { toEdited: (v) => v / 33.86389, toStored: (v) => v * 33.86389 },
  },
  volume: {
    gal: { toEdited: (v) => v / 3.78541, toStored: (v) => v * 3.78541 },
  },
};

/** How one custom alarm's value boxes read, and how they convert back. */
export interface CustomValueUnits {
  /** True when the box shows something other than the stored number. */
  readonly converted: boolean;
  /**
   * The label beside the box — the account's unit when converting, the signal's
   * declared store unit as-is otherwise (`''` when the boat declares none).
   */
  readonly unitSuffix: string;
  /** The pinned signal's declared store unit — what the wire carries. */
  readonly storeUnit: string | null;
  /** What the numbers measure. Absent ⇒ nothing converts. */
  readonly category?: UnitCategory;
  /** The account settings the conversion resolved against. Absent ⇒ identity. */
  readonly settings?: UnitSettings;
  /** The stored number as the operator should read it. */
  readonly toEdited: (stored: number) => number;
  /** The operator's number as it must be stored. */
  readonly toStored: (edited: number) => number;
}

const IDENTITY_CONVERSION: ValueConversion = { toEdited: (v) => v, toStored: (v) => v };

function resolveValueUnits(
  signalPath: string | undefined,
  storeUnitRaw: string | null | undefined,
  settings: UnitSettings | undefined
): CustomValueUnits {
  const storeUnit = storeUnitRaw ?? null;
  const identity: CustomValueUnits = {
    converted: false,
    unitSuffix: (storeUnit ?? '').trim(),
    storeUnit,
    ...IDENTITY_CONVERSION,
  };
  if (settings === undefined) return identity;
  const category = categoryFor(signalPath, storeUnit);
  if (category === undefined) return identity;
  // ONE gate, asked of `displayUnits` itself rather than restated here: it
  // declines when the boat's declared unit is not the category's store unit,
  // and when the account's unit for that category already IS it. Asking the
  // same question the read-only surfaces ask is what keeps the box and the
  // reading beside it in one unit.
  if (convertedQuantity(1, storeUnit, category, settings) === undefined) return identity;
  const conversions = CATEGORY_CONVERSIONS[category] as Partial<Record<string, ValueConversion>>;
  const conversion = conversions[settings[category]];
  // Unreachable while the table covers every firing pair (pinned by test). If
  // it ever is reached, the box stays in store units — an honest readout in the
  // boat's unit, never a converted label over an unconverted number.
  if (conversion === undefined) return identity;
  return {
    converted: true,
    unitSuffix: convertQuantity(0, storeUnit, category, settings).unit,
    storeUnit,
    category,
    settings,
    toEdited: conversion.toEdited,
    toStored: conversion.toStored,
  };
}

/**
 * How this draft's value boxes read — the ONE resolver for the suffix beside
 * them, the text inside them, and the arithmetic back to the wire.
 *
 * A surface rendering anything else about the same row (the live reading, a
 * sparkline) must take `category` and `storeUnit` from here and hand them to
 * `displayUnits`/`alarmLive` rather than re-deriving them, or the two halves of
 * one row can disagree about what unit they are in.
 */
export function customValueUnits(draft: CustomAlarmDraft): CustomValueUnits {
  return resolveValueUnits(draft.signalPath, draft.storeUnit, draft.units);
}

/**
 * A stored number as the string to put IN the value box.
 *
 * Converted, it is rounded to the unit's own display precision — "66" for a
 * stored 20 m read in feet — and deliberately does NOT round-trip: the shortest
 * decimal that does is "65.6167979", which is not a number anyone would type.
 * What makes that safe is the baseline recorded beside it; read
 * {@link draftSetpointStored} before changing either.
 *
 * Unconverted, it is `String(stored)` — byte for byte what this form has always
 * put in the box.
 */
export function customValueText(draft: CustomAlarmDraft, stored: number): string {
  return valueTextIn(customValueUnits(draft), stored);
}

function valueTextIn(units: CustomValueUnits, stored: number): string {
  if (!Number.isFinite(stored)) return '';
  const shown = convertedQuantity(stored, units.storeUnit, units.category, units.settings);
  if (shown === undefined) return String(stored);
  // `toFixed` pads ("66.0"); an input box wants the plain number back.
  const rounded = Number(shown.text);
  return Number.isFinite(rounded) ? String(rounded) : shown.text;
}

/**
 * What one value box means ON THE WIRE. Three answers, in order (ADR 0189 §4):
 *
 *   a. The box still holds the text it opened with ⇒ the ORIGINAL stored
 *      number, verbatim. No arithmetic runs, so no artefact can exist.
 *   b. The text changed but means the same number ("26.00" for "26", a stray
 *      space) ⇒ the original stored number again. Retyping what was already
 *      there is not an edit.
 *   c. Otherwise ⇒ converted exactly once, through the same resolver the box
 *      was rendered with.
 *
 * (b) compares against the BASELINE TEXT rather than a re-conversion of the
 * baseline number: the question is "is this still the number that was on
 * screen", and the screen only ever showed the text.
 */
function storedFromBox(
  draft: CustomAlarmDraft,
  raw: string | undefined,
  baseline: CustomValueBaseline | undefined
): number | undefined {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  if (baseline !== undefined) {
    if (trimmed === baseline.text) return baseline.stored;
    const original = Number(baseline.text);
    if (Number.isFinite(original) && original === n) return baseline.stored;
  }
  return customValueUnits(draft).toStored(n);
}

/**
 * The set point IN STORE UNITS — what the wire carries and what any comparison
 * against a boat reading must use.
 *
 * EXPORTED because every surface that reads a draft's set point has to resolve
 * it the same way. `Number(draft.setpoint)` is a bug once the account reads in
 * feet: it takes the box's number for a stored one, so it stores 66 metres, or
 * converts an already-converted number a second time. On a draft with no units
 * this returns exactly `Number(draft.setpoint)`, so a call site can switch to it
 * before anything threads units through.
 */
export function draftSetpointStored(draft: CustomAlarmDraft): number | undefined {
  return storedFromBox(draft, draft.setpoint, draft.baseline?.setpoint);
}

/** The clear point in store units — see {@link draftSetpointStored}. */
export function draftClearpointStored(draft: CustomAlarmDraft): number | undefined {
  return storedFromBox(draft, draft.clearpoint, draft.baseline?.clearpoint);
}

/**
 * What a caller must know for a draft's boxes to read in the account's unit:
 * the account's settings, and the DECLARED unit of the signal the alarm is
 * pinned to (from the catalog row — a stored definition does not carry it).
 *
 * Either one missing leaves the boxes in store units, which is a correct
 * readout, just not the operator's preferred one. Passing settings without the
 * store unit is the quiet failure to watch for: the store-unit gate declines,
 * so nothing converts, and it looks exactly like a metric account.
 */
export interface CustomUnitContext {
  readonly units?: UnitSettings;
  readonly storeUnit?: string | null;
}

// --- field actions (pure; each returns a new draft) ------------------------

/**
 * Pin a signal. Changing the signal DROPS the set/clear points: a threshold is
 * meaningful only against the signal it was typed for, and carrying "26" from a
 * battery voltage onto an oil pressure would silently author 26 Pa. The trigger
 * class survives only when the new signal's kind still supports it (a High on a
 * switch is not a thing). Delays are unit-free, so they stay.
 *
 * The new signal's DECLARED UNIT comes along, because it decides how the empty
 * boxes will read. Their baselines go with the numbers they described: a
 * baseline left behind would, on a coincidental retype of the old signal's set
 * point, re-emit the OLD signal's stored number onto the new one.
 */
export function setSignal(draft: CustomAlarmDraft, signal: Signal): CustomAlarmDraft {
  if (draft.signalPath === signal.path) return draft;
  const stillValid =
    draft.triggerClass !== undefined &&
    suggestedTriggerClasses(signal.kind, signal.path).includes(draft.triggerClass);
  return {
    ...draft,
    signalPath: signal.path,
    storeUnit: signal.unit,
    triggerClass: stillValid ? draft.triggerClass : undefined,
    setpoint: undefined,
    clearpoint: undefined,
    baseline: undefined,
  };
}

export function setTriggerClass(
  draft: CustomAlarmDraft,
  triggerClass: TriggerClass
): CustomAlarmDraft {
  return { ...draft, triggerClass };
}

export function setSetpoint(draft: CustomAlarmDraft, setpoint: string): CustomAlarmDraft {
  return { ...draft, setpoint };
}

export function setClearpoint(draft: CustomAlarmDraft, clearpoint: string): CustomAlarmDraft {
  return { ...draft, clearpoint };
}

export function setSetDelaySeconds(draft: CustomAlarmDraft, seconds: string): CustomAlarmDraft {
  return { ...draft, setDelaySeconds: seconds };
}

export function setClearDelaySeconds(draft: CustomAlarmDraft, seconds: string): CustomAlarmDraft {
  return { ...draft, clearDelaySeconds: seconds };
}

export function setStaleSeconds(draft: CustomAlarmDraft, seconds: string): CustomAlarmDraft {
  return { ...draft, staleSeconds: seconds };
}

/**
 * Set the name. Typing marks the title touched so the sentence prefill stops
 * overwriting it; CLEARING the field un-touches it, so emptying the box hands
 * the prefill back rather than leaving the operator with a required-field error
 * and no way to recover the suggestion.
 */
export function setTitle(draft: CustomAlarmDraft, title: string): CustomAlarmDraft {
  return { ...draft, title, titleTouched: title.trim().length > 0 };
}

export function setSeverity(draft: CustomAlarmDraft, severity: AlarmSeverity): CustomAlarmDraft {
  return { ...draft, severity };
}

export function setNotify(draft: CustomAlarmDraft, notify: NotificationUrgency): CustomAlarmDraft {
  return { ...draft, notify };
}

/**
 * Toggle a mode on/off, keeping `enabledModes` in universe order.
 *
 * The tick itself is {@link toggleWatchGridMode}, shared with the recipe form:
 * it carries `Sailing` with `Underway` exactly as the presets do, which is what
 * keeps the grid able to un-write everything a preset writes even though
 * `Sailing` has no checkbox of its own.
 */
export function toggleMode(draft: CustomAlarmDraft, mode: string): CustomAlarmDraft {
  if (!CUSTOM_MODE_UNIVERSE.includes(mode)) return draft;
  const next = toggleWatchGridMode(draft.enabledModes, mode);
  return { ...draft, enabledModes: CUSTOM_MODE_UNIVERSE.filter((m) => next.has(m)) };
}

/**
 * Replace the whole mode set at once — what choosing a watch-schedule preset
 * does (`watchSchedule.ts`). Same normalization as {@link toggleMode}: universe
 * order, deduped, and anything outside the universe dropped. A preset can never
 * write a mode the checkbox grid could not un-write: the one mode the grid does
 * not offer is `Sailing`, and {@link toggleMode} takes that off with `Underway`.
 */
export function setEnabledModes(
  draft: CustomAlarmDraft,
  modes: readonly string[]
): CustomAlarmDraft {
  const wanted = new Set(modes);
  return { ...draft, enabledModes: CUSTOM_MODE_UNIVERSE.filter((m) => wanted.has(m)) };
}

// --- condition-row actions (pure; each returns a new draft) ----------------

/** Replace one condition row, leaving the draft alone when the index is out of range. */
function withCondition(
  draft: CustomAlarmDraft,
  index: number,
  next: (row: ConditionDraft) => ConditionDraft
): CustomAlarmDraft {
  const row = draft.conditions[index];
  if (row === undefined) return draft;
  const replaced = next(row);
  if (replaced === row) return draft;
  return {
    ...draft,
    conditions: draft.conditions.map((c, i) => (i === index ? replaced : c)),
  };
}

/**
 * Append an empty condition row. At the cap the draft comes back unchanged, so
 * a caller that forgot to hide "Add another condition" cannot author a fifth
 * clause the API would reject.
 */
export function addCondition(draft: CustomAlarmDraft): CustomAlarmDraft {
  if (draft.conditions.length >= MAX_CONDITIONS) return draft;
  return { ...draft, conditions: [...draft.conditions, {}] };
}

/** Drop one condition row. Out-of-range leaves the draft alone. */
export function removeCondition(draft: CustomAlarmDraft, index: number): CustomAlarmDraft {
  if (index < 0 || index >= draft.conditions.length) return draft;
  return { ...draft, conditions: draft.conditions.filter((_, i) => i !== index) };
}

/**
 * Pin a condition row's signal. Same rule as the trigger's {@link setSignal}:
 * the comparison value is DROPPED, because "500" typed against engine rpm means
 * nothing against a battery voltage, and the operator keeps their operator only
 * when the new signal's kind can still carry it.
 */
export function setConditionSignal(
  draft: CustomAlarmDraft,
  index: number,
  signal: Signal
): CustomAlarmDraft {
  return withCondition(draft, index, (row) => {
    if (row.signalPath === signal.path) return row;
    const operators = conditionOperatorsFor(signal.kind);
    const keep = row.operator !== undefined && operators.includes(row.operator);
    return {
      signalPath: signal.path,
      operator: keep ? row.operator : undefined,
      value: undefined,
    };
  });
}

export function setConditionOperator(
  draft: CustomAlarmDraft,
  index: number,
  operator: ConditionOperator
): CustomAlarmDraft {
  return withCondition(draft, index, (row) => ({ ...row, operator }));
}

export function setConditionValue(
  draft: CustomAlarmDraft,
  index: number,
  value: string
): CustomAlarmDraft {
  return withCondition(draft, index, (row) => ({ ...row, value }));
}

export function setConditionLogic(
  draft: CustomAlarmDraft,
  conditionLogic: ConditionLogic
): CustomAlarmDraft {
  return { ...draft, conditionLogic };
}

/**
 * Keep the name in step with the review sentence while the operator has not
 * named the alarm themselves. The caller passes the already-composed suggestion
 * (`defaultTitle` from `alarmSentence.ts`) so this module stays free of display
 * concerns; an untouched draft adopts it, a touched one is left alone.
 */
export function syncAutoTitle(draft: CustomAlarmDraft, suggestion: string): CustomAlarmDraft {
  if (draft.titleTouched || draft.title === suggestion) return draft;
  return { ...draft, title: suggestion };
}

// --- parsing helpers -------------------------------------------------------

/** A finite number from a raw input, or undefined when blank/not a number. */
function finiteFrom(raw: string | undefined): number | undefined {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function isBlank(raw: string | undefined): boolean {
  return (raw ?? '').trim().length === 0;
}

/** Milliseconds for a seconds input; blank counts as 0 (no delay). */
function msFromSeconds(raw: string | undefined): number {
  const seconds = finiteFrom(raw);
  return seconds === undefined ? 0 : Math.round(seconds * 1000);
}

// --- validation ------------------------------------------------------------

/**
 * One condition ROW's error slot. The index is the row's position, so the
 * builder can put a message beside the row that caused it — build one with
 * {@link conditionField} and read the index back with {@link conditionIndexOf}
 * rather than formatting the string by hand.
 */
export type ConditionField = `condition:${number}`;

/** Every field the builder can put an error on, in form order. */
export type CustomAlarmField =
  | 'signalPath'
  | 'triggerClass'
  | 'setpoint'
  | 'clearpoint'
  | 'setDelaySeconds'
  | 'clearDelaySeconds'
  | 'staleSeconds'
  | ConditionField
  /** The "Only when…" section as a whole (the clause cap, the all/any toggle). */
  | 'conditions'
  | 'title'
  | 'enabledModes';

/** The error slot for condition row `index`. */
export function conditionField(index: number): ConditionField {
  return `condition:${index}`;
}

/** The row index a {@link ConditionField} points at, or `null` for any other field. */
export function conditionIndexOf(field: CustomAlarmField): number | null {
  if (!field.startsWith('condition:')) return null;
  const index = Number(field.slice('condition:'.length));
  return Number.isInteger(index) && index >= 0 ? index : null;
}

/** One field's error. At most one per field; the list is in form order. */
export interface CustomAlarmError {
  readonly field: CustomAlarmField;
  readonly message: string;
}

/** Which trigger classes a signal kind can carry, in plain English. */
function triggerHint(kind: string): string {
  if (kind === 'number') {
    return 'This signal is a number — it can go above a set point, drop below one, or stop reporting.';
  }
  if (kind === 'boolean') {
    return 'This signal is on or off — it can turn on, turn off, or stop reporting.';
  }
  return 'This signal is not a number or a switch — the only thing it can do is stop reporting.';
}

/** Which operators a signal kind can carry in a condition, in plain English. */
function conditionHint(kind: string): string {
  if (kind === 'number') {
    return 'This signal is a number — a condition can only check whether it is above or below a value.';
  }
  if (kind === 'boolean') {
    return 'This signal is on or off — a condition can only check which.';
  }
  return 'This signal is not a number or a switch, so it cannot be used as a condition.';
}

/**
 * A completed condition row as a comparison key, for spotting an exact repeat.
 * The value is the PARSED number, so "500" and "500.0" are the same clause —
 * which is what the boat would see. Incomplete rows have no key: they already
 * carry their own error, and calling two blank rows duplicates would be noise.
 */
function conditionKey(row: ConditionDraft): string | undefined {
  const path = row.signalPath?.trim();
  const operator = row.operator;
  if (path === undefined || path.length === 0 || operator === undefined) return undefined;
  if (operator === 'on' || operator === 'off') return `${path}|${operator}`;
  const value = finiteFrom(row.value);
  if (value === undefined) return undefined;
  return `${path}|${operator}|${value}`;
}

/** Range + whole-number check shared by both delays and the staleness window. */
function durationError(raw: string | undefined, noun: string): string | undefined {
  if (isBlank(raw)) return undefined;
  const seconds = finiteFrom(raw);
  if (seconds === undefined) return 'Enter a number of seconds.';
  if (seconds < 0) return `A ${noun} cannot be negative.`;
  if (!Number.isInteger(seconds)) return 'Enter a whole number of seconds.';
  if (seconds > MAX_DELAY_SECONDS) {
    return `Keep it to ${MAX_DELAY_SECONDS} seconds (24 hours) or less.`;
  }
  return undefined;
}

/**
 * Field errors for the draft, ORDERED the way the form reads (signal → trigger
 * → thresholds → name → modes) so a page can show the first one and scroll to
 * it. Empty ⇒ submittable.
 *
 * `catalog` is the boat's signal catalog — the same accepted-paths set the cloud
 * compiler binds against. A path outside it is rejected HERE rather than as a
 * 422 from the API, because the picker only ever offers catalog paths, so an
 * off-catalog draft means the signal stopped being reported while the form was
 * open.
 */
export function validate(draft: CustomAlarmDraft, catalog: readonly Signal[]): CustomAlarmError[] {
  const errors: CustomAlarmError[] = [];
  const push = (field: CustomAlarmField, message: string): void => {
    errors.push({ field, message });
  };

  const signal =
    draft.signalPath === undefined ? undefined : catalog.find((s) => s.path === draft.signalPath);

  if (draft.signalPath === undefined || draft.signalPath.trim().length === 0) {
    push('signalPath', 'Pick the signal this alarm should watch.');
  } else if (signal === undefined) {
    push('signalPath', 'This boat is not reporting that signal. Pick one from the list.');
  }

  const triggerClass = draft.triggerClass;
  if (triggerClass === undefined) {
    push('triggerClass', 'Choose what sets this alarm off.');
  } else if (
    signal !== undefined &&
    !suggestedTriggerClasses(signal.kind, signal.path).includes(triggerClass)
  ) {
    push('triggerClass', triggerHint(signal.kind));
  }

  if (triggerClass === 'High' || triggerClass === 'Low') {
    // The two boxes are compared AS TYPED, not as stored. Both hold the same
    // unit, every conversion here is increasing, and rounding to a display
    // precision preserves order — so a pair the operator can see is the wrong
    // way round is caught, and a pair that only inverts below the precision on
    // screen (two boxes both reading "66") never draws a message about numbers
    // nobody can tell apart.
    const setpoint = finiteFrom(draft.setpoint);
    if (isBlank(draft.setpoint)) {
      push('setpoint', 'Enter a set point.');
    } else if (setpoint === undefined) {
      push('setpoint', 'Enter a number.');
    }
    if (!isBlank(draft.clearpoint)) {
      const clearpoint = finiteFrom(draft.clearpoint);
      if (clearpoint === undefined) {
        push('clearpoint', 'Enter a number.');
      } else if (setpoint !== undefined) {
        // The clear side has to be reachable or the alarm could never clear.
        // The message names the direction the signal recovers in, because
        // "above/below the set point" alone is the part people get backwards.
        if (triggerClass === 'High' && clearpoint > setpoint) {
          push(
            'clearpoint',
            'This alarm clears on the way back down, so the clear point must be at or below the set point.'
          );
        }
        if (triggerClass === 'Low' && clearpoint < setpoint) {
          push(
            'clearpoint',
            'This alarm clears on the way back up, so the clear point must be at or above the set point.'
          );
        }
      }
    }
  }

  // Delays are only sent (and only rendered) for the value/switch classes —
  // validating them under DataUnavailable would trap the form on a leftover
  // bad value with no input on screen to fix it (mirror of thresholdBlock).
  if (triggerClass !== 'DataUnavailable') {
    const setDelayErr = durationError(draft.setDelaySeconds, 'delay');
    if (setDelayErr !== undefined) push('setDelaySeconds', setDelayErr);
    const clearDelayErr = durationError(draft.clearDelaySeconds, 'delay');
    if (clearDelayErr !== undefined) push('clearDelaySeconds', clearDelayErr);
  }
  if (triggerClass === 'DataUnavailable') {
    const staleErr = durationError(draft.staleSeconds, 'waiting time');
    if (staleErr !== undefined) push('staleSeconds', staleErr);
  }

  // "Only when…": every row that exists has to be complete. A row is only ever
  // on screen because the operator added it, so an unfinished one is an error
  // to fix or a row to remove — never something to quietly drop on submit.
  if (draft.conditions.length > MAX_CONDITIONS) {
    push(
      'conditions',
      `Keep it to ${MAX_CONDITIONS} conditions. Remove one before adding another.`
    );
  }
  const seenKeys = new Map<string, number>();
  draft.conditions.forEach((row, index) => {
    const field = conditionField(index);
    const path = row.signalPath?.trim();
    const rowSignal = path === undefined ? undefined : catalog.find((s) => s.path === path);
    if (path === undefined || path.length === 0) {
      push(field, 'Pick the signal this condition should watch.');
      return;
    }
    if (rowSignal === undefined) {
      push(field, 'This boat is not reporting that signal. Pick one from the list.');
      return;
    }
    const operators = conditionOperatorsFor(rowSignal.kind);
    if (operators.length === 0) {
      push(field, conditionHint(rowSignal.kind));
      return;
    }
    if (row.operator === undefined) {
      push(field, 'Choose what this condition checks.');
      return;
    }
    if (!operators.includes(row.operator)) {
      push(field, conditionHint(rowSignal.kind));
      return;
    }
    if (row.operator === 'above' || row.operator === 'below') {
      if (isBlank(row.value)) {
        push(field, 'Enter a value for this condition.');
        return;
      }
      if (finiteFrom(row.value) === undefined) {
        push(field, 'Enter a number.');
        return;
      }
    }
    const key = conditionKey(row);
    if (key === undefined) return;
    const first = seenKeys.get(key);
    if (first !== undefined) {
      push(field, `This is the same as condition ${first + 1}. Change it or remove it.`);
      return;
    }
    seenKeys.set(key, index);
  });

  const title = draft.title.trim();
  if (title.length === 0) {
    push('title', 'Give this alarm a name.');
  } else if (title.length > MAX_TITLE_LENGTH) {
    push('title', `Use ${MAX_TITLE_LENGTH} characters or fewer.`);
  }

  if (draft.enabledModes.length === 0) {
    push('enabledModes', 'Choose at least one mode to watch.');
  }

  return errors;
}

export function hasErrors(errors: readonly CustomAlarmError[]): boolean {
  return errors.length > 0;
}

/** This field's error message, or undefined. */
export function errorFor(
  errors: readonly CustomAlarmError[],
  field: CustomAlarmField
): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

// --- body builder ----------------------------------------------------------

/**
 * The reserved `thresholdsByMode` block a custom alarm's params ride in.
 * A type alias rather than an interface deliberately: that gives it an implicit
 * index signature, so a built block can be handed straight to the sentence
 * renderer, which reads STORED blocks (`Record<string, unknown>`) too.
 */
export type CustomThresholdBlock = {
  setpoint?: number;
  clearpoint?: number;
  setDelayMs?: number;
  clearDelayMs?: number;
  staleMs?: number;
};

/**
 * One condition clause as the wire takes it (`alertConditionClauseSchema`).
 * A custom alarm carries EXPLICIT path clauses only — the `engine_running`
 * preset is a recipe affordance and the API 400s it on a custom def.
 */
export type CustomConditionClauseBody =
  | { type: 'High' | 'Low'; path: string; value: number }
  | { type: 'Active' | 'Inactive'; path: string };

/** The `conditions` block: how the clauses fold, plus 1..4 clauses. */
export interface CustomConditionsBody {
  logic: ConditionLogic;
  clauses: CustomConditionClauseBody[];
}

/** The POST body for `POST /v1/boats/:boatId/alerts`, custom branch. */
export interface CustomAlarmCreateBody {
  /** `v` is stamped server-side, so the client sends path + class only. */
  custom: { path: string; triggerClass: TriggerClass };
  title: string;
  severity: AlarmSeverity;
  enabledModes: string[];
  thresholdsByMode: Record<string, CustomThresholdBlock>;
  notificationUrgencyByMode: Record<string, NotificationUrgency>;
  /** Absent when no condition was authored — an unconditional alarm sends no block. */
  conditions?: CustomConditionsBody;
}

/** The wire clause for one completed row, or `undefined` when it is unfinished. */
function conditionClauseBody(row: ConditionDraft): CustomConditionClauseBody | undefined {
  const path = row.signalPath?.trim();
  if (path === undefined || path.length === 0) return undefined;
  switch (row.operator) {
    case 'above':
    case 'below': {
      const value = finiteFrom(row.value);
      if (value === undefined) return undefined;
      return { type: row.operator === 'above' ? 'High' : 'Low', path, value };
    }
    case 'on':
      return { type: 'Active', path };
    case 'off':
      return { type: 'Inactive', path };
    default:
      return undefined;
  }
}

/**
 * The `conditions` block this draft authors, or `undefined` when it authors
 * none. What the review sentence renders and what {@link toCreateBody} sends —
 * one mapping, so the sentence can never describe a clause the wire will not
 * carry.
 *
 * Unfinished rows are skipped (they are already validation errors), and a draft
 * whose rows are ALL unfinished sends nothing: the wire schema requires at least
 * one clause, so an empty `clauses` array would be a 400 rather than the "no
 * conditions" the draft actually means.
 */
export function draftConditions(draft: CustomAlarmDraft): CustomConditionsBody | undefined {
  const clauses = draft.conditions
    .map(conditionClauseBody)
    .filter((c): c is CustomConditionClauseBody => c !== undefined);
  if (clauses.length === 0) return undefined;
  return { logic: draft.conditionLogic, clauses };
}

/**
 * The ONE threshold block this draft authors, built per trigger class so a
 * value left over from a class switch (a set point typed before the operator
 * chose "turns on") can never reach the wire. Zero-length delays are omitted
 * rather than sent as `0` — the compiler's default is the same, and an absent
 * key keeps the stored params (and the definition digest) clean.
 *
 * STORE UNITS throughout. The set and clear points come back through the
 * baseline guard, so a box the operator never touched contributes the number
 * that was already stored, bit for bit.
 */
function thresholdBlock(draft: CustomAlarmDraft, triggerClass: TriggerClass): CustomThresholdBlock {
  const block: CustomThresholdBlock = {};
  if (triggerClass === 'DataUnavailable') {
    // The staleness window IS the trigger for this class; the compiler ignores
    // set/clear delays here, so sending them would be dead weight.
    const staleSeconds = finiteFrom(draft.staleSeconds);
    if (staleSeconds !== undefined) block.staleMs = Math.round(staleSeconds * 1000);
    return block;
  }
  if (triggerClass === 'High' || triggerClass === 'Low') {
    const setpoint = draftSetpointStored(draft);
    if (setpoint !== undefined) block.setpoint = setpoint;
    const clearpoint = draftClearpointStored(draft);
    if (clearpoint !== undefined) block.clearpoint = clearpoint;
  }
  const setDelayMs = msFromSeconds(draft.setDelaySeconds);
  if (setDelayMs > 0) block.setDelayMs = setDelayMs;
  const clearDelayMs = msFromSeconds(draft.clearDelaySeconds);
  if (clearDelayMs > 0) block.clearDelayMs = clearDelayMs;
  return block;
}

/**
 * The threshold block this draft currently authors — what the review sentence
 * and the live-evaluation line render from, before the draft is complete enough
 * to build a body. Empty until a trigger class is chosen.
 */
export function draftThresholds(draft: CustomAlarmDraft): CustomThresholdBlock {
  return draft.triggerClass === undefined ? {} : thresholdBlock(draft, draft.triggerClass);
}

/**
 * The full POST body, or `null` when the draft has no signal or no trigger
 * class (call {@link validate} first — this is the type-level guard, not a
 * second validation pass).
 *
 * The single authored block is FANNED OUT across every enabled mode: the API
 * stores thresholds keyed by mode and rejects divergent blocks on a custom
 * alarm (v1 compiles one), so identical copies are the only shape that both
 * satisfies "every enabled mode carries its params" and survives the
 * single-block check. `custom.v` is omitted — the server stamps it.
 */
export function toCreateBody(draft: CustomAlarmDraft): CustomAlarmCreateBody | null {
  const path = draft.signalPath;
  const triggerClass = draft.triggerClass;
  if (path === undefined || path.trim().length === 0 || triggerClass === undefined) return null;

  const block = thresholdBlock(draft, triggerClass);
  const thresholdsByMode: Record<string, CustomThresholdBlock> = {};
  const notificationUrgencyByMode: Record<string, NotificationUrgency> = {};
  for (const mode of draft.enabledModes) {
    thresholdsByMode[mode] = { ...block };
    notificationUrgencyByMode[mode] = draft.notify;
  }

  const conditions = draftConditions(draft);

  return {
    custom: { path, triggerClass },
    title: draft.title.trim(),
    severity: draft.severity,
    enabledModes: [...draft.enabledModes],
    thresholdsByMode,
    notificationUrgencyByMode,
    ...(conditions === undefined ? {} : { conditions }),
  };
}

// --- live evaluation (plan D8) ---------------------------------------------

/** One live reading for the pinned signal — a `LiveSample` fits this shape. */
export interface LiveReading {
  /** The latest value, however the boat reported it. */
  readonly value: unknown;
  /** Epoch-ms the sample arrived (`LiveSample.receivedAt`). */
  readonly receivedAt?: number;
}

/** The evaluator's `isTruthy` — mirrored so the preview cannot disagree with it. */
function isTruthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    return v === 'on' || v === 'true' || v === '1';
  }
  return false;
}

/** The evaluator's `isFalsy` — mirrored (note: NOT the inverse of isTruthy). */
function isFalsy(value: unknown): boolean {
  if (typeof value === 'boolean') return !value;
  if (typeof value === 'number') return value === 0;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    return v === 'off' || v === 'false' || v === '0';
  }
  return false;
}

/**
 * Is this reading recent enough to make a present-tense claim from?
 *
 * The band is `signalCatalog.freshness` — the same one the picker's dot and the
 * builder's "last reported …" note read — so the page cannot say "would be
 * firing" over a note saying the signal went quiet days ago. Deliberately not a
 * second threshold of its own: one band, one story.
 *
 * An unstamped reading is not fresh. A catalog row can carry a stored value with
 * no timestamp, and "we have a number but no idea when it arrived" is exactly the
 * case a definitive claim must not be built on.
 */
function readingIsFresh(live: LiveReading | null | undefined, now: number): boolean {
  const receivedAt = live?.receivedAt;
  if (typeof receivedAt !== 'number' || !Number.isFinite(receivedAt)) return false;
  const at = new Date(receivedAt);
  if (Number.isNaN(at.getTime())) return false;
  return freshness(at.toISOString(), now) === 'fresh';
}

/** The evaluator's `isDataUnavailable` — mirrored. */
function readsAsUnavailable(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number' && !Number.isFinite(value)) return true;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'data not available' || v === 'unavailable' || v === 'n/a' || v === '';
  }
  return false;
}

/**
 * Would this alarm's trigger be met by the reading on screen right now?
 * `true` ⇒ "with the current reading this alarm would be firing"; `false` ⇒
 * "…would stay quiet"; `null` ⇒ we cannot say, and the UI must say THAT rather
 * than guess.
 *
 * The comparisons mirror the evaluator exactly (`High` fires strictly above the
 * set point, `On` uses the same truthy coercion, and so on). Three deliberate
 * limits, all about honesty:
 *
 *   - DELAYS ARE NOT MODELLED. This answers "is the trigger met", not "has it
 *     been met long enough" — a set delay only postpones the same answer.
 *   - A STALE READING IS NOT AN ANSWER. "This alarm would be firing" is a claim
 *     about right now; a value the boat last sent days ago cannot support it,
 *     and the boat would in fact hold that alarm pending until the signal
 *     reports again. So the value-based classes answer only from a reading
 *     inside {@link readingIsFresh}'s band, and return `null` otherwise.
 *   - `DataUnavailable` NEVER returns `true`. A gap in the browser's WebSocket
 *     stream is not evidence that the BOAT sees a gap, and the boat is what
 *     evaluates. A recent, real reading is decidable ("would stay quiet");
 *     anything else is `null`. This class keeps its own window — the authored
 *     staleness the operator typed — because that window IS the trigger.
 */
export function wouldFireNow(
  draft: CustomAlarmDraft,
  live: LiveReading | null | undefined,
  now: number = Date.now()
): boolean | null {
  const triggerClass = draft.triggerClass;
  if (triggerClass === undefined) return null;
  const value = live?.value;

  switch (triggerClass) {
    case 'High':
    case 'Low': {
      // BOTH SIDES IN STORE UNITS. The reading is the boat's raw number, so the
      // set point is resolved back out of the box rather than read from it — a
      // verdict taken in display units would fire an anchor alarm at 66 metres.
      const setpoint = draftSetpointStored(draft);
      if (setpoint === undefined) return null;
      if (typeof value !== 'number' || !Number.isFinite(value)) return null;
      if (!readingIsFresh(live, now)) return null;
      return triggerClass === 'High' ? value > setpoint : value < setpoint;
    }
    case 'On':
    case 'Off': {
      if (value === undefined || value === null) return null;
      if (!readingIsFresh(live, now)) return null;
      if (isTruthy(value)) return triggerClass === 'On';
      if (isFalsy(value)) return triggerClass === 'Off';
      // Recognisably neither on nor off: the evaluator holds it as not firing.
      return false;
    }
    case 'DataUnavailable': {
      if (live === null || live === undefined) return null;
      const receivedAt = live.receivedAt;
      if (typeof receivedAt !== 'number' || !Number.isFinite(receivedAt)) return null;
      const staleSeconds = finiteFrom(draft.staleSeconds) ?? DEFAULT_STALE_SECONDS;
      if (now - receivedAt > staleSeconds * 1000) return null;
      return readsAsUnavailable(value) ? null : false;
    }
  }
}

/**
 * Does one condition row hold with the reading on screen right now? `true` /
 * `false` / `null` for "cannot say", exactly like {@link wouldFireNow}, and on
 * the same freshness band — a clause chip must not read "true" over a note
 * saying the signal went quiet two days ago.
 *
 * The comparisons mirror the evaluator's clause fold (`evaluateClause`): `above`
 * is strictly greater, `below` strictly less, and a value that is recognisably
 * neither on nor off (a stray string, a non-finite number) satisfies NEITHER
 * `on` nor `off` — it reads `false`, which is the boat's fail-closed answer.
 *
 * Fail-closed is what the boat DOES; `null` is what this preview SAYS when it
 * has no reading to judge from. The two are different claims and the UI states
 * both: an unknowable clause shows "—", and the permanent note explains that on
 * the boat a clause with no data reads false, so a broken sensor silences the
 * alarm under "all".
 */
export function clauseHolds(
  cond: ConditionDraft,
  live: LiveReading | null | undefined,
  now: number = Date.now()
): boolean | null {
  const operator = cond.operator;
  if (operator === undefined) return null;
  const value = live?.value;

  switch (operator) {
    case 'above':
    case 'below': {
      const target = finiteFrom(cond.value);
      if (target === undefined) return null;
      if (typeof value !== 'number' || !Number.isFinite(value)) return null;
      if (!readingIsFresh(live, now)) return null;
      return operator === 'above' ? value > target : value < target;
    }
    case 'on':
    case 'off': {
      if (value === undefined || value === null) return null;
      if (!readingIsFresh(live, now)) return null;
      // A non-finite number is not a real reading: the evaluator rejects it for
      // Active AND Inactive, so neither clause holds.
      if (typeof value === 'number' && !Number.isFinite(value)) return false;
      if (isTruthy(value)) return operator === 'on';
      if (isFalsy(value)) return operator === 'off';
      return false;
    }
  }
}

/**
 * Fold per-clause verdicts the way the boat does (`evaluateQualifierVerdict`),
 * three-way:
 *
 *   - `all`: any `false` ⇒ false; else any unknown ⇒ unknown; else true.
 *   - `any`: any `true` ⇒ true; else any unknown ⇒ unknown; else false.
 *
 * NO clauses ⇒ `true`: an alarm with no conditions is unconditional. (That is
 * not the evaluator's empty-clause-list case, which cannot happen here — a draft
 * with no rows sends no `conditions` block at all.)
 */
export function foldConditions(
  verdicts: readonly (boolean | null)[],
  logic: ConditionLogic
): boolean | null {
  if (verdicts.length === 0) return true;
  if (logic === 'any') {
    if (verdicts.includes(true)) return true;
    return verdicts.includes(null) ? null : false;
  }
  if (verdicts.includes(false)) return false;
  return verdicts.includes(null) ? null : true;
}

/**
 * Where to find the current reading for a path. A `Map` (what
 * `useBoatChrome().liveSamples` is shaped like, once resolved through
 * `signalLive.signalReading`) or a lookup function — whichever the caller
 * already has.
 */
export type ReadingLookup =
  | ReadonlyMap<string, LiveReading | null | undefined>
  | ((path: string) => LiveReading | null | undefined);

function readingFor(
  readings: ReadingLookup | null | undefined,
  path: string | undefined
): LiveReading | null | undefined {
  if (readings === null || readings === undefined) return undefined;
  if (path === undefined || path.length === 0) return undefined;
  return typeof readings === 'function' ? readings(path) : readings.get(path);
}

/** Each condition row's live verdict, in row order — one chip per row. */
export function conditionVerdicts(
  draft: CustomAlarmDraft,
  readings: ReadingLookup | null | undefined,
  now: number = Date.now()
): (boolean | null)[] {
  return draft.conditions.map((row) => clauseHolds(row, readingFor(readings, row.signalPath), now));
}

/**
 * The whole alarm's live answer: the trigger AND the folded conditions, with
 * the same three-way honesty as {@link wouldFireNow}.
 *
 * `false` wins over `null` on BOTH legs, because a definite no is a definite no:
 * a trigger that is not met keeps the alarm quiet however the conditions read,
 * and a condition that is definitely false does the same. Only when nothing is
 * definitely false and something is unknowable does the answer become `null` —
 * which the UI must render as "we cannot tell", never as "would stay quiet".
 */
export function combinedWouldFire(
  draft: CustomAlarmDraft,
  readings: ReadingLookup | null | undefined,
  now: number = Date.now()
): boolean | null {
  const trigger = wouldFireNow(draft, readingFor(readings, draft.signalPath), now);
  if (trigger === false) return false;
  const qualifier = foldConditions(conditionVerdicts(draft, readings, now), draft.conditionLogic);
  if (qualifier === false) return false;
  if (trigger === null || qualifier === null) return null;
  return true;
}

// --- reading a STORED definition back into a draft (phase 7) ----------------

/** The v1 trigger classes, for checking a class string off the wire. */
const TRIGGER_CLASSES: ReadonlySet<string> = new Set<TriggerClass>([
  'High',
  'Low',
  'On',
  'Off',
  'DataUnavailable',
]);

/** The notification urgencies the shared vocabulary defines. */
const URGENCIES: ReadonlySet<string> = new Set<NotificationUrgency>([
  'local',
  'remote',
  'escalating',
  'silent',
]);

/**
 * The severities the shared vocabulary defines, read from {@link ALARM_SEVERITIES}
 * rather than listed here — a hand-written list silently dropped `warning`, and a
 * dropped severity does not fail: it downgrades the handed-over draft to the
 * default without saying so.
 */
const SEVERITIES: ReadonlySet<string> = new Set<string>(ALARM_SEVERITIES);

/**
 * The stored definition fields this module reads. A structural subset of the
 * detail page's `AlarmDefView`, so a test can build one from a plain object and
 * the page can hand its definition straight over.
 */
export interface StoredCustomDef {
  readonly title: string;
  readonly severity: AlarmSeverity | null;
  readonly enabledModes: readonly string[];
  readonly thresholdsByMode: Record<string, Record<string, unknown>>;
  readonly notificationUrgencyByMode?: Record<string, unknown>;
  readonly custom?: unknown;
  readonly conditions?: unknown;
}

/**
 * One STORED clause as an editable row — the exact inverse of
 * {@link conditionClauseBody} (`High`→above, `Low`→below, `Active`→on,
 * `Inactive`→off).
 *
 * `undefined` for a clause this form cannot author: the `engine_running` preset
 * (a recipe affordance — the API rejects it on a custom def, and the compiler
 * needs an engine binding this form has none of) or a clause shape from a newer
 * build. The caller must treat that as "not editable here" rather than dropping
 * the clause, because saving a draft that silently lost a clause would REMOVE a
 * gate the operator never touched.
 */
export function conditionDraftFromClause(clause: ConditionClauseSpec): ConditionDraft | undefined {
  if ('preset' in clause) return undefined;
  const path = typeof clause.path === 'string' ? clause.path.trim() : '';
  if (path.length === 0) return undefined;
  switch (clause.type) {
    case 'High':
    case 'Low': {
      const value = clause.value;
      if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
      return {
        signalPath: path,
        operator: clause.type === 'High' ? 'above' : 'below',
        value: String(value),
      };
    }
    case 'Active':
      return { signalPath: path, operator: 'on' };
    case 'Inactive':
      return { signalPath: path, operator: 'off' };
    default:
      return undefined;
  }
}

/** Seconds, as a raw input string, for a stored millisecond value. */
function secondsFrom(ms: unknown): string | undefined {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return undefined;
  return String(ms / 1000);
}

/**
 * The threshold block a stored definition authors: the first enabled mode's,
 * else the first present — the same pick `alarmDisplay.customAlarmSetup` renders
 * from, so the editor opens on the numbers the detail page was just showing.
 * (v1 stores identical blocks across the enabled modes, so the pick only matters
 * for a definition written by something other than this builder.)
 */
function storedBlock(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, unknown>>
): Record<string, unknown> {
  for (const mode of enabledModes) {
    const block = thresholdsByMode[mode];
    if (block !== undefined && block !== null && typeof block === 'object') return block;
  }
  for (const block of Object.values(thresholdsByMode)) {
    if (block !== undefined && block !== null && typeof block === 'object') return block;
  }
  return {};
}

/**
 * One stored threshold as the box that will show it: the text, and the baseline
 * pairing that text with the number it came from. `undefined` when the stored
 * block carries nothing usable there, which is what leaves the box empty.
 */
function storedValueBox(
  units: CustomValueUnits,
  stored: unknown
): { text: string; baseline: CustomValueBaseline } | undefined {
  if (typeof stored !== 'number' || !Number.isFinite(stored)) return undefined;
  const text = valueTextIn(units, stored);
  if (text.length === 0) return undefined;
  return { text, baseline: { text, stored } };
}

/** The urgency a stored definition notifies at, or `remote` when it says nothing. */
function storedUrgency(
  enabledModes: readonly string[],
  byMode: Record<string, unknown> | undefined
): NotificationUrgency {
  if (byMode === undefined || byMode === null) return 'remote';
  const modes = [...enabledModes, ...Object.keys(byMode)];
  for (const mode of modes) {
    const value = byMode[mode];
    if (typeof value === 'string' && URGENCIES.has(value)) return value as NotificationUrgency;
  }
  return 'remote';
}

/**
 * A stored custom definition as an editable draft, or `null` when this build
 * must not offer to edit it. Four honest refusals, all of the same kind — an
 * editor that saved a draft it could not fully represent would DELETE the part
 * it could not read:
 *
 *   - no readable `custom` block (not a custom alarm, or corrupt);
 *   - a trigger class outside the v1 five (written by a newer build);
 *   - a condition clause this form cannot author (a preset, or a newer shape);
 *   - a stored `conditions` block with clauses this build dropped as unreadable.
 *
 * The name comes back TOUCHED: an edit must never have its title quietly
 * rewritten by the sentence prefill when a set point changes.
 *
 * `ctx` is the account's units and the pinned signal's declared store unit —
 * both, or the value boxes cannot be read in the operator's unit (the account
 * says fathoms; only the catalog says the boat is reporting metres). With it,
 * the boxes convert and each records the baseline that keeps an untouched save
 * bit-identical. Without it — every caller that predates ADR 0189 — the draft
 * comes back exactly as it always did, stored numbers and no baseline.
 */
export function initFromCustomDef(
  def: StoredCustomDef,
  ctx?: CustomUnitContext
): CustomAlarmDraft | null {
  const custom = readCustomSpec(def);
  if (custom === undefined) return null;
  if (!TRIGGER_CLASSES.has(custom.triggerClass)) return null;
  const triggerClass = custom.triggerClass as TriggerClass;

  const block = storedBlock(def.enabledModes, def.thresholdsByMode);
  const setpoint = block.setpoint;
  const clearpoint = block.clearpoint;

  const stored = readConditions(def.conditions);
  const conditions: ConditionDraft[] = [];
  if (stored !== undefined) {
    // A block whose clauses this build could not read is not editable: saving
    // would drop them (see `readConditions`' `dropped` count).
    if ((stored.dropped ?? 0) > 0) return null;
    for (const clause of stored.clauses) {
      const row = conditionDraftFromClause(clause);
      if (row === undefined) return null;
      conditions.push(row);
    }
    if (conditions.length > MAX_CONDITIONS) return null;
  }

  // How the two value boxes will read, decided ONCE for this draft. Both boxes
  // describe the same signal, so one answer covers them.
  const valueUnits = resolveValueUnits(custom.path, ctx?.storeUnit, ctx?.units);
  const setpointText = storedValueBox(valueUnits, setpoint);
  const clearpointText = storedValueBox(valueUnits, clearpoint);
  // A baseline only where a conversion could cost precision. An unconverted box
  // holds `String(stored)`, which parses back to the same number, so a guard
  // there would be a second way of saying the same thing.
  const baseline: CustomValueBaselines = valueUnits.converted
    ? {
        ...(setpointText === undefined ? {} : { setpoint: setpointText.baseline }),
        ...(clearpointText === undefined ? {} : { clearpoint: clearpointText.baseline }),
      }
    : {};

  const enabledLower = new Set(def.enabledModes.map((m) => m.toLowerCase()));
  return {
    signalPath: custom.path,
    triggerClass,
    ...(setpointText === undefined ? {} : { setpoint: setpointText.text }),
    ...(clearpointText === undefined ? {} : { clearpoint: clearpointText.text }),
    ...(ctx?.units === undefined ? {} : { units: ctx.units }),
    ...(ctx?.storeUnit === undefined ? {} : { storeUnit: ctx.storeUnit }),
    ...(Object.keys(baseline).length === 0 ? {} : { baseline }),
    setDelaySeconds: secondsFrom(block.setDelayMs) ?? '0',
    clearDelaySeconds: secondsFrom(block.clearDelayMs) ?? '0',
    staleSeconds: secondsFrom(block.staleMs) ?? String(DEFAULT_STALE_SECONDS),
    title: def.title,
    titleTouched: true,
    severity: def.severity ?? 'warning',
    // Case-insensitive against the universe: a definition stored with the
    // FTUE's lowercase mode names must still read as enabled.
    enabledModes: CUSTOM_MODE_UNIVERSE.filter((m) => enabledLower.has(m.toLowerCase())),
    notify: storedUrgency(def.enabledModes, def.notificationUrgencyByMode),
    conditions,
    conditionLogic: stored?.logic === 'any' ? 'any' : 'all',
  };
}

// --- the PATCH body (changed fields only) ----------------------------------

/**
 * The PATCH body for `PATCH /v1/boats/:boatId/alerts/:alertId`, custom branch.
 * Every field is optional: the API leaves an absent one alone, and re-sending an
 * unchanged one would recompile the boat's ruleset for nothing.
 */
export interface CustomAlarmPatchBody {
  title?: string;
  severity?: AlarmSeverity;
  enabledModes?: string[];
  thresholdsByMode?: Record<string, CustomThresholdBlock>;
  notificationUrgencyByMode?: Record<string, NotificationUrgency>;
  /** All-or-nothing, like the API: a custom block is replaced whole, never patched. */
  custom?: { path: string; triggerClass: TriggerClass };
  /** `null` CLEARS the stored conditions; an object replaces them. */
  conditions?: CustomConditionsBody | null;
}

function sameModeSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((m) => sb.has(m));
}

/**
 * Whether the fanned-out threshold map means what is already stored. Compared
 * key by key rather than by `JSON.stringify`, so key ORDER can never make an
 * unchanged alarm look edited — the API replaces the WHOLE map, so a spurious
 * diff rewrites and recompiles the definition.
 */
function sameThresholdMaps(
  next: Record<string, CustomThresholdBlock>,
  prev: Record<string, Record<string, unknown>>
): boolean {
  const nextModes = Object.keys(next).sort();
  const prevModes = Object.keys(prev).sort();
  if (nextModes.length !== prevModes.length) return false;
  if (nextModes.some((m, i) => m !== prevModes[i])) return false;
  for (const mode of nextModes) {
    const nm = (next[mode] ?? {}) as Record<string, number | undefined>;
    const pm = prev[mode] ?? {};
    const nmKeys = Object.keys(nm)
      .filter((k) => nm[k] !== undefined)
      .sort();
    const pmKeys = Object.entries(pm)
      .filter(([, v]) => typeof v === 'number')
      .map(([k]) => k)
      .sort();
    if (nmKeys.length !== pmKeys.length) return false;
    if (nmKeys.some((k, i) => k !== pmKeys[i])) return false;
    for (const key of nmKeys) {
      if (nm[key] !== pm[key]) return false;
    }
  }
  return true;
}

function sameUrgencyMaps(
  next: Record<string, NotificationUrgency>,
  prev: Record<string, unknown> | undefined
): boolean {
  const prevEntries = Object.entries(prev ?? {}).filter(([, v]) => typeof v === 'string');
  const nextEntries = Object.entries(next);
  if (nextEntries.length !== prevEntries.length) return false;
  return nextEntries.every(([mode, urgency]) => (prev ?? {})[mode] === urgency);
}

/** One clause as a comparable string — order-independent, like the recipe form's. */
function clauseKey(clause: CustomConditionClauseBody): string {
  return 'value' in clause
    ? `${clause.type}:${clause.path}:${clause.value}`
    : `${clause.type}:${clause.path}`;
}

/** Whether two conditions blocks mean the same thing (clause order does not count). */
function sameConditions(
  next: CustomConditionsBody | undefined,
  prev: CustomConditionsBody | undefined
): boolean {
  if (next === undefined || prev === undefined) return next === prev;
  if (next.logic !== prev.logic) return false;
  const a = next.clauses.map(clauseKey).sort();
  const b = prev.clauses.map(clauseKey).sort();
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

/** The stored `conditions` block in the shape {@link draftConditions} returns. */
function storedConditions(raw: unknown): CustomConditionsBody | undefined {
  const stored = readConditions(raw);
  if (stored === undefined) return undefined;
  const clauses: CustomConditionClauseBody[] = [];
  for (const clause of stored.clauses) {
    if ('preset' in clause) continue;
    if (clause.type === 'High' || clause.type === 'Low') {
      if (typeof clause.value !== 'number') continue;
      clauses.push({ type: clause.type, path: clause.path, value: clause.value });
      continue;
    }
    clauses.push({ type: clause.type, path: clause.path });
  }
  if (clauses.length === 0) return undefined;
  return { logic: stored.logic ?? 'all', clauses };
}

/**
 * The PATCH body — CHANGED FIELDS ONLY, diffed against the stored definition.
 *
 * `thresholdsByMode`, `notificationUrgencyByMode` and `custom` are each
 * all-or-nothing because the API replaces them whole; `conditions` sends an
 * explicit `null` when the last condition row is removed (the API's clear), and
 * nothing at all when the block is unchanged. An unchanged draft therefore
 * produces `{}` — {@link isEmptyPatch} — and the caller skips the request rather
 * than bumping the boat's ruleset version for no edit.
 *
 * THE DIFF IS TAKEN IN STORE UNITS on both sides: `thresholdBlock` resolves the
 * boxes back through the baseline guard, and the result is compared against the
 * stored map. That is what keeps an untouched save empty for an account reading
 * in feet, and — because `thresholdsByMode` is replaced whole — what keeps an
 * edit to the delay from rewriting the set point beside it by a rounding
 * artefact.
 */
export function toPatchBody(
  draft: CustomAlarmDraft,
  original: StoredCustomDef
): CustomAlarmPatchBody {
  const patch: CustomAlarmPatchBody = {};

  const title = draft.title.trim();
  if (title.length > 0 && title !== original.title) patch.title = title;
  if (draft.severity !== original.severity) patch.severity = draft.severity;
  if (!sameModeSet(draft.enabledModes, original.enabledModes)) {
    patch.enabledModes = [...draft.enabledModes];
  }

  const triggerClass = draft.triggerClass;
  const path = draft.signalPath?.trim();
  if (triggerClass !== undefined && path !== undefined && path.length > 0) {
    const stored = readCustomSpec(original);
    if (stored === undefined || stored.path !== path || stored.triggerClass !== triggerClass) {
      patch.custom = { path, triggerClass };
    }
    const block = thresholdBlock(draft, triggerClass);
    const thresholdsByMode: Record<string, CustomThresholdBlock> = {};
    const urgencyByMode: Record<string, NotificationUrgency> = {};
    for (const mode of draft.enabledModes) {
      thresholdsByMode[mode] = { ...block };
      urgencyByMode[mode] = draft.notify;
    }
    if (!sameThresholdMaps(thresholdsByMode, original.thresholdsByMode)) {
      patch.thresholdsByMode = thresholdsByMode;
    }
    if (!sameUrgencyMaps(urgencyByMode, original.notificationUrgencyByMode)) {
      patch.notificationUrgencyByMode = urgencyByMode;
    }
  }

  const nextConditions = draftConditions(draft);
  if (!sameConditions(nextConditions, storedConditions(original.conditions))) {
    patch.conditions = nextConditions ?? null;
  }

  return patch;
}

/** Nothing changed — skip the request entirely. */
export function isEmptyPatch(patch: CustomAlarmPatchBody): boolean {
  return Object.keys(patch).length === 0;
}

// --- duplicate for another instance (plan D3) ------------------------------

/**
 * The same alarm aimed at a SIBLING path — the other engine's oil pressure, the
 * other bank's voltage (`signalCatalog.siblingSignals` finds them).
 *
 * The thresholds are KEPT, deliberately: a sibling is the same measurement in
 * the same unit, which is the entire point of the offer — dropping them the way
 * {@link setSignal} does would leave the operator retyping numbers they can see
 * on the page they came from. The NAME is cleared and un-touched so the builder
 * prefills a name for the new signal rather than opening with the old engine's
 * name on the new engine's alarm.
 *
 * The CONDITION ROWS are carried over VERBATIM — pointing at the paths they were
 * authored against. Re-aiming them at the sibling's instance would be a guess
 * ("only while engine 1 is running" might well be exactly what a second alarm on
 * engine 2 is meant to say), and a silently re-pointed safety gate is worse than
 * one the operator can see: the builder opens with every row on screen, its live
 * reading beside it, and the review sentence naming it in full.
 *
 * The unit context and the BASELINES are kept for the same reason the numbers
 * are: a sibling reports the same measurement in the same unit, so the copy
 * should be stored as the identical number — not as a re-conversion of the
 * rounded text on screen (20 m, never 20.1168 m).
 */
export function duplicateForPath(draft: CustomAlarmDraft, path: string): CustomAlarmDraft {
  return { ...draft, signalPath: path, title: '', titleTouched: false };
}

/**
 * One handed-over baseline, or `undefined` for anything that is not the pair.
 * Both halves are checked: a text with no number, or a number that is not
 * finite, would make {@link storedFromBox} answer with garbage for a box the
 * operator never touched.
 */
function readBaseline(raw: unknown): CustomValueBaseline | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.text !== 'string' || r.text.trim().length === 0) return undefined;
  if (typeof r.stored !== 'number' || !Number.isFinite(r.stored)) return undefined;
  return { text: r.text, stored: r.stored };
}

/**
 * A draft handed over through router state, read defensively. Router state is
 * whatever was in `history.state` — a stale entry from an older build, or a
 * hand-edited one — so every field is checked and anything unrecognised falls
 * back to the fresh-draft default. `null` when there is no prefill at all.
 *
 * THE UNIT CONTEXT COMES ACROSS TOO, and it has to. "Duplicate for the other
 * engine" hands over a whole draft, so its set point may read "66" in feet; a
 * prefill rebuilt without the units that produced it would take that for metres
 * and author a 66 m depth alarm — a 3.3× error, silently, from a button that
 * promises the same alarm on the sibling. The baselines come with it, which also
 * makes the copy store the original number rather than a re-conversion.
 *
 * The account's settings are validated by the shared `parseUnitSettings`, which
 * answers `null` — never a half-filled object — for anything but the exact seven
 * categories.
 */
export function readDraftPrefill(state: unknown): CustomAlarmDraft | null {
  if (state === null || typeof state !== 'object') return null;
  const raw = (state as { customPrefill?: unknown }).customPrefill;
  if (raw === null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const base = initCustomDraft();

  const path = typeof p.signalPath === 'string' ? p.signalPath.trim() : '';
  const triggerClass =
    typeof p.triggerClass === 'string' && TRIGGER_CLASSES.has(p.triggerClass)
      ? (p.triggerClass as TriggerClass)
      : undefined;
  const str = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value : undefined;
  const modes = Array.isArray(p.enabledModes)
    ? CUSTOM_MODE_UNIVERSE.filter((m) => (p.enabledModes as unknown[]).includes(m))
    : base.enabledModes;
  const rows = Array.isArray(p.conditions) ? p.conditions.slice(0, MAX_CONDITIONS) : [];
  const conditions: ConditionDraft[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const rowPath = typeof r.signalPath === 'string' ? r.signalPath.trim() : '';
    const operator = r.operator;
    if (rowPath.length === 0) continue;
    if (operator !== 'above' && operator !== 'below' && operator !== 'on' && operator !== 'off') {
      continue;
    }
    conditions.push({
      signalPath: rowPath,
      operator,
      ...(str(r.value) !== undefined ? { value: String(r.value) } : {}),
    });
  }

  const units = parseUnitSettings(p.units);
  const setpointBaseline = readBaseline(
    (p.baseline as Record<string, unknown> | undefined)?.setpoint
  );
  const clearpointBaseline = readBaseline(
    (p.baseline as Record<string, unknown> | undefined)?.clearpoint
  );
  const baseline: CustomValueBaselines = {
    ...(setpointBaseline === undefined ? {} : { setpoint: setpointBaseline }),
    ...(clearpointBaseline === undefined ? {} : { clearpoint: clearpointBaseline }),
  };

  return {
    ...base,
    ...(path.length > 0 ? { signalPath: path } : {}),
    ...(triggerClass !== undefined ? { triggerClass } : {}),
    ...(str(p.setpoint) !== undefined ? { setpoint: String(p.setpoint) } : {}),
    ...(str(p.clearpoint) !== undefined ? { clearpoint: String(p.clearpoint) } : {}),
    ...(units === null ? {} : { units }),
    ...(typeof p.storeUnit === 'string' || p.storeUnit === null ? { storeUnit: p.storeUnit } : {}),
    ...(Object.keys(baseline).length === 0 ? {} : { baseline }),
    setDelaySeconds: str(p.setDelaySeconds) ?? base.setDelaySeconds,
    clearDelaySeconds: str(p.clearDelaySeconds) ?? base.clearDelaySeconds,
    staleSeconds: str(p.staleSeconds) ?? base.staleSeconds,
    severity:
      typeof p.severity === 'string' && SEVERITIES.has(p.severity)
        ? (p.severity as AlarmSeverity)
        : base.severity,
    enabledModes: modes.length > 0 ? modes : base.enabledModes,
    notify:
      typeof p.notify === 'string' && URGENCIES.has(p.notify)
        ? (p.notify as NotificationUrgency)
        : base.notify,
    conditions,
    conditionLogic: p.conditionLogic === 'any' ? 'any' : 'all',
  };
}
